import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import axios, { AxiosResponse } from 'axios'
import FormData from 'form-data'
// import dayjs from 'dayjs'

import { authApi } from '@store/slices/auth.slice'
import { useGetItemFromLocalStorage } from '@hooks/app/localStorage/useGetItemFromLocalStorage'

import {
  ICheckConnectionSpeedResponse,
  IGetAddDeviceCodeResponse,
  IRightMenuInitialState,
} from '@assets/types/right-menu.types'
import { TLanguage } from '@assets/types/app.types'
import { axiosBaseQuery } from '@store/axiosBaseQuery'
import { IError } from '@store/axiosBaseQueryTyped'
import { USER_TOKEN } from '@utils/vars'
import { getTimeNow } from '@utils/getTimeNow'

const initialState: IRightMenuInitialState = {
  mainMenu: {
    isOpened: false,
    items: {
      promotionsIsActive: false,
      parentalControlIsActive: false,
      paymentIsActive: false,
      plansIsActive: false,
      settingsIsActive: false,
      devicesIsActive: false,
      appInfoIsActive: false,
    },
  },
  promotionsMenu: {
    isOpened: false,
    isActive: false,
    activePromotionIdx: 0,
  },
  subMenu: {
    isOpened: false,
    payment: {
      isShown: false,
      isActive: false,
      balance: {
        isActive: false,
      },
      voucher: {
        isActive: false,
      },
    },
    plans: {
      isShown: false,
      isActive: false,
      isUpsale: false,
      activePlanIdx: 0,
    },
    settings: {
      isShown: false,
      isActive: false,
      language: {
        isActive: false,
        position: 'ua',
      },
      channelNameInTheList: {
        isActive: false,
      },
      age: {
        isActive: false,
      },
      resetSettings: {
        isActive: false,
      },
      checkSpeed: {
        isActive: false,
      },
      logout: {
        isActive: false,
      },
    },
    devices: {
      isShown: false,
      isActive: false,
      activeDeviceIdx: 0,
      addDeviceBtn: false,
    },
    appInfo: {
      isShown: false,
      isActive: false,
      privacyPolicy: {
        isActive: false,
      },
    },
  },
}

export const appRightMenuSlice = createSlice({
  name: 'appRightMenu',
  initialState,
  reducers: {
    // Main menu
    appRightMenu_reset: () => initialState,
    appRightMenu_mainMeu_partialReset: (state) => {
      state.mainMenu.items = initialState.mainMenu.items
    },
    appRightMenu_mainMenu_setMainMenuIsOpened: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.isOpened = action.payload
    },
    appRightMenu_mainMenu_items_setParentalControlIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.items.parentalControlIsActive = action.payload
    },
    appRightMenu_mainMenu_items_setPaymentIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.items.paymentIsActive = action.payload
    },
    appRightMenu_mainMenu_items_setPlansIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.items.plansIsActive = action.payload
    },
    appRightMenu_mainMenu_items_setSettingsIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.items.settingsIsActive = action.payload
    },
    appRightMenu_mainMenu_items_setDevicesIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.items.devicesIsActive = action.payload
    },
    appRightMenu_mainMenu_items_setAppInfoIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.items.appInfoIsActive = action.payload
    },
    appRightMenu_mainMenu_items_setPromotionsIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mainMenu.items.promotionsIsActive = action.payload
    },

    // Promotions menu
    appRightMenu_promotionsMenu_reset: (state) => {
      state.promotionsMenu = initialState.promotionsMenu
    },
    appRightMenu_promotionsMenu_openPromotionsMenu: (state) => {
      if (!state.promotionsMenu.isOpened) {
        state.promotionsMenu.isOpened = true
      } else {
        state.promotionsMenu.isActive = !state.promotionsMenu.isActive
        state.promotionsMenu.activePromotionIdx = 0
        state.mainMenu.items.promotionsIsActive = false
      }
      if (state.subMenu.isOpened) state.subMenu.isOpened = false
    },
    appRightMenu_promotionsMenu_incrementActivePromotionIdx: (state) => {
      state.promotionsMenu.activePromotionIdx++
    },
    appRightMenu_promotionsMenu_decrementActivePromotionIdx: (state) => {
      state.promotionsMenu.activePromotionIdx--
    },

    // Sub menu
    appRightMenu_subMenu_setIsOpened: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.isOpened = action.payload
    },
    appRightMenu_subMenu_partialReset: (state) => {
      state.subMenu.appInfo = initialState.subMenu.appInfo
      state.subMenu.devices = initialState.subMenu.devices
      state.subMenu.payment = initialState.subMenu.payment
      state.subMenu.plans = {
        ...initialState.subMenu.plans,
        isUpsale: state.subMenu.plans.isUpsale,
      }
      state.subMenu.settings = initialState.subMenu.settings
    },
    appRightMenu_subMenu_reset: (state) => {
      state.subMenu = initialState.subMenu
    },

    // payment
    appRightMenu_subMenu_payment_click: (state) => {
      if (state.mainMenu.items.paymentIsActive)
        state.mainMenu.items.paymentIsActive = false
      if (!state.subMenu.payment.isActive) state.subMenu.payment.isActive = true
    },
    appRightMenu_subMenu_payment_openPayment: (state) => {
      state.subMenu.payment.isShown = true
      if (!state.subMenu.isOpened) state.subMenu.isOpened = true
      if (state.promotionsMenu.isOpened) state.promotionsMenu = initialState.promotionsMenu
    },
    appRightMenu_subMenu_payment_togglePaymentIsActive: (state) => {
      state.mainMenu.items.paymentIsActive =
        !state.mainMenu.items.paymentIsActive
      state.subMenu.payment.isActive = !state.mainMenu.items.paymentIsActive
    },
    appRightMenu_subMenu_payment_partialReset: (state) => {
      state.subMenu.payment.balance = initialState.subMenu.payment.balance
      state.subMenu.payment.voucher = initialState.subMenu.payment.voucher
    },
    appRightMenu_subMenu_payment_balance_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.payment.balance.isActive = action.payload
    },
    appRightMenu_subMenu_payment_voucher_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.payment.voucher.isActive = action.payload
    },

    // plans
    appRightMenu_subMenu_plans_openPlans: (state) => {
      state.subMenu.plans.isShown = true
      if (!state.subMenu.isOpened) state.subMenu.isOpened = true
      if (state.promotionsMenu.isOpened) state.promotionsMenu = initialState.promotionsMenu
    },
    appRightMenu_subMenu_plans_togglePlansIsActive: (state) => {
      state.mainMenu.items.plansIsActive = !state.mainMenu.items.plansIsActive
      state.subMenu.plans.isActive = !state.subMenu.plans.isActive
    },
    appRightMenu_subMenu_plans_incrementActivePlanIdx: (state) => {
      state.subMenu.plans.activePlanIdx++
    },
    appRightMenu_subMenu_plans_decrementActivePlanIdx: (state) => {
      state.subMenu.plans.activePlanIdx--
    },
    appRightMenu_subMenu_plans_setActivePlanIdx: (
      state,
      action: PayloadAction<number>
    ) => {
      state.subMenu.plans.activePlanIdx = action.payload
    },
    appRightMenu_subMenu_plans_setIsUpsale: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.plans.isUpsale = action.payload
    },
    appRightMenu_subMenu_plans_click: (
      state,
      action: PayloadAction<number>
    ) => {
      if (state.mainMenu.items.plansIsActive)
        state.mainMenu.items.plansIsActive = false
      if (!state.subMenu.plans.isActive) state.subMenu.plans.isActive = true
      state.subMenu.plans.activePlanIdx = action.payload
    },

    // settings
    appRightMenu_subMenu_settings_partialReset: (state) => {
      state.subMenu.settings.language = initialState.subMenu.settings.language
      state.subMenu.settings.channelNameInTheList =
        initialState.subMenu.settings.channelNameInTheList
      state.subMenu.settings.resetSettings =
        initialState.subMenu.settings.resetSettings
      state.subMenu.settings.logout = initialState.subMenu.settings.logout
      state.subMenu.settings.checkSpeed =
        initialState.subMenu.settings.checkSpeed
    },
    appRightMenu_subMenu_settings_openSettings: (state) => {
      state.subMenu.settings.isShown = true
      if (!state.subMenu.isOpened) state.subMenu.isOpened = true
      if (state.promotionsMenu.isOpened) state.promotionsMenu = initialState.promotionsMenu
    },
    appRightMenu_subMenu_settings_toggleSettingsIsActive: (state) => {
      state.mainMenu.items.settingsIsActive =
        !state.mainMenu.items.settingsIsActive
      state.subMenu.settings.isActive = !state.subMenu.settings.isActive
    },
    appRightMenu_subMenu_settings_click: (state) => {
      if (state.mainMenu.items.settingsIsActive)
        state.mainMenu.items.settingsIsActive = false
      if (!state.subMenu.settings.isActive)
        state.subMenu.settings.isActive = true
    },

    // language
    appRightMenu_subMenu_settings_language_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.settings.language.isActive = action.payload
    },
    appRightMenu_subMenu_settings_language_setPosition: (
      state,
      action: PayloadAction<TLanguage>
    ) => {
      state.subMenu.settings.language.position = action.payload
    },
    appRightMenu_subMenu_settings_language_clickHandler: (state) => {
      state.subMenu.settings.language.isActive = true
      state.subMenu.settings.channelNameInTheList.isActive = false
      state.subMenu.settings.age.isActive = false
      state.subMenu.settings.resetSettings.isActive = false
      state.subMenu.settings.checkSpeed.isActive = false
      state.subMenu.settings.logout.isActive = false
    },

    // user settings
    appRightMenu_subMenu_settings_channelNameInTheList_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.settings.channelNameInTheList.isActive = action.payload
    },
    appRightMenu_subMenu_settings_age_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.settings.age.isActive = action.payload
    },
    appRightMenu_subMenu_settings_channelNameInTheList_clickHandler: (
      state
    ) => {
      state.subMenu.settings.channelNameInTheList.isActive = true
      state.subMenu.settings.age.isActive = false
      state.subMenu.settings.language.isActive = false
      state.subMenu.settings.resetSettings.isActive = false
      state.subMenu.settings.checkSpeed.isActive = false
      state.subMenu.settings.logout.isActive = false
    },
    appRightMenu_subMenu_settings_twentyOneYearsOld_clickHandler: (state) => {
      state.subMenu.settings.age.isActive = true
      state.subMenu.settings.channelNameInTheList.isActive = false
      state.subMenu.settings.language.isActive = false
      state.subMenu.settings.resetSettings.isActive = false
      state.subMenu.settings.checkSpeed.isActive = false
      state.subMenu.settings.logout.isActive = false
    },

    // reset settings
    appRightMenu_subMenu_settings_resetSettings_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.settings.resetSettings.isActive = action.payload
    },
    appRightMenu_subMenu_settings_resetSettings_clickHandler: (state) => {
      state.subMenu.settings.resetSettings.isActive = true
      state.subMenu.settings.language.isActive = false
      state.subMenu.settings.channelNameInTheList.isActive = false
      state.subMenu.settings.age.isActive = false
      state.subMenu.settings.checkSpeed.isActive = false
      state.subMenu.settings.logout.isActive = false
    },

    // speed
    appRightMenu_subMenu_settings_checkSpeed_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.settings.checkSpeed.isActive = action.payload
    },

    appRightMenu_subMenu_settings_checkSpeed_clickHandler: (state) => {
      state.subMenu.settings.checkSpeed.isActive = true
      state.subMenu.settings.resetSettings.isActive = false
      state.subMenu.settings.language.isActive = false
      state.subMenu.settings.channelNameInTheList.isActive = false
      state.subMenu.settings.age.isActive = false
      state.subMenu.settings.logout.isActive = false
    },

    // logout
    appRightMenu_subMenu_settings_logout_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.settings.logout.isActive = action.payload
    },
    appRightMenu_subMenu_settings_logout_clickHandler: (state) => {
      state.subMenu.settings.logout.isActive = true
      state.subMenu.settings.language.isActive = false
      state.subMenu.settings.channelNameInTheList.isActive = false
      state.subMenu.settings.age.isActive = false
      state.subMenu.settings.resetSettings.isActive = false
      state.subMenu.settings.checkSpeed.isActive = false
    },

    // devices
    appRightMenu_subMenu_devices_openDevices: (state) => {
      state.subMenu.devices.isShown = true
      if (!state.subMenu.isOpened) state.subMenu.isOpened = true
      if (state.promotionsMenu.isOpened) state.promotionsMenu = initialState.promotionsMenu
    },
    appRightMenu_subMenu_devices_toggleDevicesIsActive: (state) => {
      state.mainMenu.items.devicesIsActive =
        !state.mainMenu.items.devicesIsActive
      state.subMenu.devices.isActive = !state.subMenu.devices.isActive
    },
    appRightMenu_subMenu_devices_incrementActiveDeviceIdx: (state) => {
      state.subMenu.devices.activeDeviceIdx++
    },
    appRightMenu_subMenu_devices_decrementActiveDeviceIdx: (state) => {
      state.subMenu.devices.activeDeviceIdx--
    },
    appRightMenu_subMenu_devices_setActiveDeviceIdx: (
      state,
      action: PayloadAction<number>
    ) => {
      state.subMenu.devices.activeDeviceIdx = action.payload
    },
    appRightMenu_subMenu_devices_deviceClick: (
      state,
      action: PayloadAction<number>
    ) => {
      if (state.mainMenu.items.devicesIsActive)
        state.mainMenu.items.devicesIsActive = false
      if (!state.subMenu.devices.isActive) state.subMenu.devices.isActive = true
      state.subMenu.devices.activeDeviceIdx = action.payload
      if (state.subMenu.devices.addDeviceBtn)
        state.subMenu.devices.addDeviceBtn = false
    },
    appRightMenu_subMenu_devices_setAddDeviceBtnIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.devices.addDeviceBtn = action.payload
    },

    // app info
    appRightMenu_subMenu_appInfo_openAppInfo: (state) => {
      state.subMenu.appInfo.isShown = true
      if (!state.subMenu.isOpened) state.subMenu.isOpened = true
      if (state.promotionsMenu.isOpened) state.promotionsMenu = initialState.promotionsMenu
    },
    appRightMenu_subMenu_appInfo_toggleAppInfoIsActive: (state) => {
      state.mainMenu.items.appInfoIsActive =
        !state.mainMenu.items.appInfoIsActive
      state.subMenu.appInfo.isActive = !state.subMenu.appInfo.isActive
      state.subMenu.appInfo.privacyPolicy.isActive =
        !state.subMenu.appInfo.privacyPolicy.isActive
    },
    appRightMenu_subMenu_appInfo_privacyPolicy_setIsActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.subMenu.appInfo.privacyPolicy.isActive = action.payload
    },
    appRightMenu_subMenu_appInfo_privacyPolicy_click: (state) => {
      if (state.mainMenu.items.appInfoIsActive)
        state.mainMenu.items.appInfoIsActive = false
      if (!state.subMenu.appInfo.isActive) state.subMenu.appInfo.isActive = true
      state.subMenu.appInfo.privacyPolicy.isActive = true
    },
  },
})

export const appRightMenuApi = createApi({
  reducerPath: 'appRightMenuApi',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.BASE_CDN_URL,
  }),
  endpoints: (builder) => ({
    check_connection_speed: builder.query<ICheckConnectionSpeedResponse, void>({
      queryFn: async () => {
        const url = `${
          process.env.BASE_CDN_URL
        }/monitoring/stream?_=${getTimeNow()}`

        const startTime = performance.now()

        try {
          const response = await axios.get(url, {
            responseType: 'arraybuffer',
          })

          const endTime = performance.now()

          const size = Number(
            response.headers['content-length'] || response.data?.byteLength
          )

          const host = response.headers['node'] || null

          const duration =
            (endTime - startTime === 0 ? 1 : endTime - startTime) / 1000
          const bitsLoaded = size * 8
          const speedBps = Math.round((bitsLoaded / duration) * 100) / 100
          const speedKbps = Math.round((speedBps / 1024) * 100) / 100
          const speedMbps = Math.round((speedKbps / 1024) * 100) / 100

          const { data } = await axios.get<string>(
            'https://cdnua01.hls.tv/myip.html'
          )

          return {
            data: {
              speed: isNaN(speedMbps) ? null : speedMbps,
              ip: data || null,
              host,
            },
          }
        } catch (error) {
          return { error }
        }
      },
      keepUnusedDataFor: 0,
    }),
    get_add_device_code: builder.query<IGetAddDeviceCodeResponse, void>({
      queryFn: async (_, _api) => {
        const userToken = useGetItemFromLocalStorage(USER_TOKEN)
        const request = new FormData()
        request.append('token', userToken)
        const url = `${process.env.BASE_API_URL}/device/activation_code`

        try {
          const { data } = await axios.post<IGetAddDeviceCodeResponse>(
            url,
            request
          )
          if (data.code === '1704') {
            _api.dispatch(
              authApi.util.invalidateTags(['user_info', 'profile_page'])
            )
          }

          return { data }
        } catch (error) {
          const { request } = error as AxiosResponse
          const { status, statusText } = request as IError

          return {
            error: {
              status,
              statusText,
            },
          }
        }
      },
    }),
  }),
})

export const appRightMenuReducer = appRightMenuSlice.reducer
export const appRightMenuActions = appRightMenuSlice.actions

export const {
  useLazyCheck_connection_speedQuery,
  useLazyGet_add_device_codeQuery,
} = appRightMenuApi
